/* eslint-disable @typescript-eslint/naming-convention */
import { FeatureFlag, Site, Size } from '@ps/web-core-enums';
import type { RegulatoryHeader } from '@ps/web-martech-cms-types';
import { useScreenSize } from '@ps/web-shared-components';
import classNames from 'clsx';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import useFeature from '@swf/core/hooks/useFeature';
import { select } from '@swf/core/store';

import headerWidgetStyles from '../../CMSHeader.scss';
import PanicButtonVegas from '../PanicButtonVegas/PanicButtonVegas';

import styles from './TopHeader.scss';
import RegulatoryHeaderCAON from './components/RegulatoryHeaderCAON/RegulatoryHeaderCAON';
import RegulatoryHeaderCH from './components/RegulatoryHeaderCH/RegulatoryHeaderCH';
import RegulatoryHeaderDESH from './components/RegulatoryHeaderDESH/RegulatoryHeaderDESH';
import RegulatoryHeaderES from './components/RegulatoryHeaderES/RegulatoryHeaderES';
import RegulatoryHeaderFR from './components/RegulatoryHeaderFR/RegulatoryHeaderFR';
import RegulatoryHeaderIT from './components/RegulatoryHeaderIT/RegulatoryHeaderIT';
import SessionHeader from './components/SessionHeader/SessionHeader';

export interface Props {
  regulatory_header: RegulatoryHeader[];
}

type ComponentProps = Props & { index: number };

const TopHeader: React.FC<Props> = ({ regulatory_header }) => {
  const site = useSelector(select.cms.getSiteId);
  const isSiteUS = useSelector(select.cms.isSiteUS);
  const isMobile = useSelector(select.device.isMobile);
  const isAuthenticated = useSelector(select.session.isAuthenticated);
  const DEPanic = useFeature(FeatureFlag.DEPanic);

  const { greaterThan } = useScreenSize();

  const components = useMemo(
    () =>
      [
        {
          condition:
            [Site.DK, Site.PT, Site.SE, Site.EE, Site.BE, Site.NL, Site.CZ].includes(site) ||
            isSiteUS,
          render: (props: ComponentProps) => <SessionHeader {...props} />
        },
        {
          condition: site === Site.IT,
          render: (props: ComponentProps) => <RegulatoryHeaderIT {...props} />
        },
        {
          condition: site === Site.CH,
          render: (props: ComponentProps) => <RegulatoryHeaderCH {...props} />
        },
        {
          condition: site === Site.DESH,
          render: (props: ComponentProps) => <RegulatoryHeaderDESH {...props} />
        },
        {
          condition: site === Site.FR,
          render: (props: ComponentProps) => <RegulatoryHeaderFR {...props} />
        },
        {
          condition: (isMobile || !greaterThan(Size.Small)) && DEPanic && isAuthenticated,
          render: ({ index }: ComponentProps) => <PanicButtonVegas key={index} />
        },
        {
          condition: site === Site.CAON,
          render: (props: ComponentProps) => <RegulatoryHeaderCAON {...props} />
        },
        {
          condition: site === Site.ES,
          render: (props: ComponentProps) => <RegulatoryHeaderES {...props} />
        }
      ].filter(({ condition }) => condition),
    [site, isSiteUS, isMobile, greaterThan, DEPanic, isAuthenticated]
  );

  return components.length ? (
    <div
      className={classNames(headerWidgetStyles.inner, styles.topHeader, {
        [styles.paddingRestriction]: site === Site.SE,
        [styles.multilineHeader]: site === Site.SE,
        [styles.reorderHeader]: [Site.USPA, Site.USMI].includes(site)
      })}
    >
      {components.map(({ render }, index) => render({ index, regulatory_header }))}
    </div>
  ) : null;
};

export default TopHeader;
